import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Building2, Mail, Phone, Linkedin, Briefcase, MapPin } from 'lucide-react';
import { Prospect } from '@/pages/People';

interface ProspectCardProps {
  prospect: Prospect;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
}

export const ProspectCard = ({ prospect, isSelected, onToggleSelect }: ProspectCardProps) => {
  return (
    <Card className="p-4 hover:border-primary/50 transition-all">
      <div className="flex items-start gap-4">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(prospect.id)}
          className="mt-1"
        />
        
        <div className="flex-1 space-y-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{prospect.name}</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Briefcase className="h-3 w-3" />
              <span>{prospect.title}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-gray-900">{prospect.company}</span>
            {prospect.industry && (
              <Badge variant="secondary" className="text-xs">
                {prospect.industry}
              </Badge>
            )}
          </div>

          {prospect.location && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-3 w-3" />
              <span>{prospect.location}</span>
            </div>
          )}

          <div className="space-y-1">
            {prospect.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-3 w-3 text-muted-foreground" />
                <a 
                  href={`mailto:${prospect.email}`} 
                  className="text-primary hover:underline"
                >
                  {prospect.email}
                </a>
              </div>
            )}
            
            {prospect.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-3 w-3 text-muted-foreground" />
                <a 
                  href={`tel:${prospect.phone}`} 
                  className="text-gray-600 hover:text-gray-900"
                >
                  {prospect.phone}
                </a>
              </div>
            )}
            
            {prospect.linkedinUrl && (
              <div className="flex items-center gap-2 text-sm">
                <Linkedin className="h-3 w-3 text-muted-foreground" />
                <a 
                  href={prospect.linkedinUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  View LinkedIn Profile
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
